import React, { ReactNode } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MINE_MODEL_KEY,
  MINE_MODEL_SUMMARY_KEY,
  RETICULATION_DATA_EXPORT_KEY
} from 'providers/api/useMineModels';
import { RECIPES_KEY } from 'providers/api/useRecipes';
import { STOPE_BACKFILLS_KEY } from 'providers/api/usePours';
import { useScenariosQuery } from 'providers/api/useScenarios';
import { resetStopeFillSceneState } from 'modules/project/ProjectAreaPage/ProjectStopesPage/Scenes/StopeFillScene/stopeFillSceneState';
import ScenarioContext, { IScenarioContext } from './context';

interface ScenarioProviderProps {
  children: ReactNode;
  projectId?: string | null;
}

const SWITCH_DURATION_MS = 700;

export const SCENARIO_URL_PARAM = 'scenario';

export const buildSearchWithScenario = (search: string, scenarioId: string | null): string => {
  const params = new URLSearchParams(search);
  if (scenarioId) {
    params.set(SCENARIO_URL_PARAM, scenarioId);
  } else {
    params.delete(SCENARIO_URL_PARAM);
  }
  const next = params.toString();
  return next ? `?${next}` : '';
};

const ScenarioProvider = ({ children, projectId }: ScenarioProviderProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeScenarioId, setActiveScenarioId] = React.useState<string | null>(null);
  const [activeScenarioName, setActiveScenarioName] = React.useState<string | null>(null);
  const [isSwitching, setIsSwitching] = React.useState<boolean>(false);
  const [switchTarget, setSwitchTarget] = React.useState<string | null>(null);
  const switchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasResolvedUrlScenarioRef = React.useRef<boolean>(false);
  const locationRef = React.useRef(location);
  locationRef.current = location;

  const requestedScenarioId = new URLSearchParams(location.search).get(SCENARIO_URL_PARAM);

  const { data: scenarios, isFetched, isError } = useScenariosQuery(
    { mineModelId: projectId ?? '' },
    { enabled: !!requestedScenarioId },
  );
  const scenarioListSettled = isFetched || isError;

  const isResolvingScenario = !!projectId
    && !!requestedScenarioId
    && requestedScenarioId !== activeScenarioId
    && !scenarioListSettled;

  const applyScenario = React.useCallback((scenarioId: string | null, scenarioName: string | null, announce: boolean) => {
    resetStopeFillSceneState();

    if (announce) {
      setSwitchTarget(scenarioName ?? 'Master');
      setIsSwitching(true);
    }

    setActiveScenarioId(scenarioId);
    setActiveScenarioName(scenarioName);

    queryClient.invalidateQueries(MINE_MODEL_KEY, { exact: false });
    queryClient.invalidateQueries(MINE_MODEL_SUMMARY_KEY, { exact: false });
    queryClient.invalidateQueries(RETICULATION_DATA_EXPORT_KEY, { exact: false });
    queryClient.invalidateQueries(RECIPES_KEY, { exact: false });
    queryClient.invalidateQueries(STOPE_BACKFILLS_KEY, { exact: false });

    if (!announce) return;

    if (switchTimerRef.current) {
      clearTimeout(switchTimerRef.current);
    }
    switchTimerRef.current = setTimeout(() => {
      setIsSwitching(false);
      switchTimerRef.current = null;
    }, SWITCH_DURATION_MS);
  }, [queryClient]);

  const writeScenarioToUrl = React.useCallback((scenarioId: string | null) => {
    const { pathname, search, hash } = locationRef.current;
    const nextSearch = buildSearchWithScenario(search, scenarioId);
    if (nextSearch === search) return;
    navigate({ pathname, search: nextSearch, hash }, { replace: true });
  }, [navigate]);

  const setActiveScenario = React.useCallback((scenarioId: string | null, scenarioName: string | null) => {
    if (scenarioId === activeScenarioId) return;

    hasResolvedUrlScenarioRef.current = true;
    applyScenario(scenarioId, scenarioName, true);
    writeScenarioToUrl(scenarioId);
  }, [activeScenarioId, applyScenario, writeScenarioToUrl]);

  const value: IScenarioContext = React.useMemo(() => ({
    activeScenarioId,
    activeScenarioName,
    isSwitching,
    isResolvingScenario,
    switchTarget,
    setActiveScenario,
  }), [activeScenarioId, activeScenarioName, isSwitching, isResolvingScenario, switchTarget, setActiveScenario]);

  React.useEffect(() => {
    if (hasResolvedUrlScenarioRef.current) return;
    if (!projectId || !requestedScenarioId) return;

    if (activeScenarioId !== null) {
      hasResolvedUrlScenarioRef.current = true;
      return;
    }

    if (!scenarioListSettled) return;

    hasResolvedUrlScenarioRef.current = true;

    const match = (scenarios ?? []).find((scenario) => scenario.scenarioId === requestedScenarioId);

    if (match?.scenarioId) {
      applyScenario(match.scenarioId, match.name ?? null, false);
      return;
    }

    writeScenarioToUrl(null);
  }, [
    projectId,
    requestedScenarioId,
    activeScenarioId,
    scenarioListSettled,
    scenarios,
    applyScenario,
    writeScenarioToUrl,
  ]);

  React.useEffect(() => () => {
    if (switchTimerRef.current) {
      clearTimeout(switchTimerRef.current);
    }
  }, []);

  return (
    <ScenarioContext.Provider value={value}>
      {children}
    </ScenarioContext.Provider>
  );
};

export default ScenarioProvider;
